// LL Design Studio — frontend. Mounts the real Printful EDM editor, lets the
// customer design a product, saves it to a templateId, and "adds to cart"
// (POSTs the line-item contract the order-router will read on orders/paid).
import { EDM } from '@fyul/embed-sdk';

// Where the "Made in Canada" track sends customers for a custom quote / to talk to
// a person. The retail studio is self-serve POD; Canadian-made custom is handled by
// the B2B custom site.
const CUSTOM_SITE_URL = 'https://locallaundry-custom.ca/pages/custom';

// Official Canada flag maple leaf (Wikimedia Maple_Leaf.svg), brand red to match the
// retail site's badge. Used wherever the studio shows a "Made in Canada" mark.
const MAPLE_SVG =
  '<svg class="maple" viewBox="-2015 -2000 4030 4030" aria-hidden="true"><path fill="#d32f2f" d="m-90 2030 45-863a95 95 0 0 0-111-98l-859 151 116-320a65 65 0 0 0-20-73l-941-762 212-99a65 65 0 0 0 34-79l-186-572 542 115a65 65 0 0 0 73-38l105-247 423 454a65 65 0 0 0 111-57l-204-1052 327 189a65 65 0 0 0 91-27l332-652 332 652a65 65 0 0 0 91 27l327-189-204 1052a65 65 0 0 0 111 57l423-454 105 247a65 65 0 0 0 73 38l542-115-186 572a65 65 0 0 0 34 79l212 99-941 762a65 65 0 0 0-20 73l116 320-859-151a95 95 0 0 0-111 98l45 863z"/></svg>';

type ProductOption = {
  productId: number;
  label: string;
  blurb: string;
  type?: string;
  image?: string | null;
  priceFrom?: number;
  colors?: number;
};

// POD set — the published lds-pod products, loaded from /api/pod/catalog at startup.
// Falls back to these staples if the endpoint is unavailable.
let POD_PRODUCTS: ProductOption[] = [
  { productId: 71, label: 'Unisex Staple Tee', blurb: 'from $29.99', type: 'T-Shirts', priceFrom: 29.99 },
  { productId: 438, label: 'Classic Tee', blurb: 'from $23.99', type: 'T-Shirts', priceFrom: 23.99 },
  { productId: 146, label: 'Pullover Hoodie', blurb: 'from $59.99', type: 'Hoodies', priceFrom: 59.99 },
];

async function loadPodCatalog(): Promise<void> {
  try {
    const res = await fetch(`/api/pod/catalog?region=${currentRegion}`);
    if (!res.ok) return;
    const items = (await res.json()) as ProductOption[];
    if (Array.isArray(items) && items.length) POD_PRODUCTS = items;
  } catch {
    /* keep fallback */
  }
}

const $ = (id: string) => document.getElementById(id) as HTMLElement;

// Deep-link: /pages/design-studio?product=71 opens the designer on that product.
// &lock=1 hides the picker (used when arriving from a specific product's PDP).
const params = new URLSearchParams(location.search);
const deepLinkProductId = params.get('product') ? Number(params.get('product')) : null;
const lockToProduct = params.get('lock') === '1' || (deepLinkProductId != null && !params.has('lock'));
// &color=<name> (from a PDP swatch) seeds the designer's starting colour. Applied
// once, on the first mount of the deep-linked product, so the customer can still
// change it afterward (and a technique re-mount doesn't snap it back).
const deepLinkColor = params.get('color') || null;
let startColorApplied = false;

// Per-placement surcharges + cap, loaded from the backend (config/addon-pricing.json).
// The base price covers ONE print; each extra placement adds its surcharge line.
type AddonDef = { label: string; variantId: string; surcharge: number };
let ADDON: { placements: Record<string, AddonDef>; methods: Record<string, AddonDef>; maxPlacements: number; primary: string } = {
  placements: {}, methods: {}, maxPlacements: 3, primary: 'front',
};
async function loadAddonPricing(): Promise<void> {
  try {
    const r = await fetch('/api/addon/pricing');
    if (r.ok) {
      const j = await r.json();
      ADDON = {
        placements: j.placements ?? {},
        methods: j.methods ?? {},
        maxPlacements: j.maxPlacements ?? 3,
        primary: j.primary ?? 'front',
      };
    }
  } catch { /* keep defaults */ }
}

// Decoration methods (DTG / DTF / Embroidery / …) available for the active product.
let TECHNIQUES: { key: string; name: string }[] = [{ key: 'dtg', name: 'Direct-to-garment print' }];
let currentTechnique = 'dtg';
async function loadTechniques(productId: number): Promise<void> {
  try {
    const r = await fetch(`/api/pod/techniques?product=${productId}`);
    if (r.ok) { const t = await r.json(); if (Array.isArray(t) && t.length) TECHNIQUES = t; }
  } catch { /* keep default */ }
  if (!TECHNIQUES.some((t) => t.key === currentTechnique)) currentTechnique = TECHNIQUES[0].key;
}

let edm: EDM | null = null;
let activeProductId = deepLinkProductId ?? POD_PRODUCTS[0].productId;
// Customer's ship-to region — only colours/products that fulfill here are offered, so
// the order routes to a LOCAL Printful facility (no cross-border duties). Seeded from
// the storefront's detected country via the ?region= deep-link; defaults to CA.
const deepLinkRegion = params.get('region');
let currentRegion: 'ca' | 'us' | 'intl' =
  deepLinkRegion === 'us' || deepLinkRegion === 'intl' || deepLinkRegion === 'ca' ? deepLinkRegion : 'ca';

async function disabledColorsForRegion(productId: number): Promise<string[]> {
  try {
    const r = await fetch(`/api/pod/region-config?product=${productId}&region=${currentRegion}`);
    if (!r.ok) return [];
    return (await r.json()).disabledColors ?? [];
  } catch {
    return [];
  }
}
let designValid = false;
let selectedVariantIds: number[] = [];
let usedPlacements: string[] = [];

// Placements charged extra = every used placement except the one included in the
// base (the primary/front if used, else the first). Front-only -> no surcharge.
function extraPlacements(): string[] {
  if (usedPlacements.length <= 1) return [];
  const primary = usedPlacements.includes(ADDON.primary) ? ADDON.primary : usedPlacements[0];
  let skipped = false;
  return usedPlacements.filter((p) => {
    if (!skipped && p === primary) { skipped = true; return false; }
    return true;
  });
}
function surchargeDef(placement: string): AddonDef {
  // Unknown placement (e.g. an embroidery spot) -> a safe default surcharge.
  return ADDON.placements[placement] ?? { label: placement, variantId: '', surcharge: 7 };
}
function techniqueName(key: string): string {
  return TECHNIQUES.find((t) => t.key === key)?.name ?? key;
}
// Method upcharge for the chosen technique (e.g. embroidery costs more than DTG).
function methodSurcharge(): AddonDef | null {
  return ADDON.methods?.[currentTechnique] ?? null;
}
function basePrice(): number {
  return POD_PRODUCTS.find((x) => x.productId === activeProductId)?.priceFrom ?? 0;
}
// Live price the customer sees update as they add placements.
function updateLivePrice(): void {
  const el = document.getElementById('live-price');
  if (!el) return;
  const extras = extraPlacements();
  const placementSurcharge = extras.reduce((s, p) => s + (surchargeDef(p).surcharge || 0), 0);
  const method = methodSurcharge();
  const total = basePrice() + placementSurcharge + (method?.surcharge ?? 0);
  el.textContent = total ? `$${total.toFixed(2)}` : '';
  const bits: string[] = [];
  if (extras.length) bits.push(`${extras.length} extra print${extras.length > 1 ? 's' : ''}`);
  if (method) bits.push(method.label.toLowerCase());
  el.title = bits.length ? `1 print + ${bits.join(' + ')}` : 'single print';
}

async function getNonce(): Promise<string> {
  const res = await fetch('/api/printful/nonce', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customerId: 'demo-customer' }),
  });
  if (!res.ok) throw new Error('Could not start the designer (nonce mint failed).');
  const { nonce } = (await res.json()) as { nonce: string };
  return nonce;
}

async function mountDesigner(productId: number, technique = currentTechnique) {
  activeProductId = productId;
  currentTechnique = technique;
  designValid = false;
  usedPlacements = [];
  setSaveEnabled(false);
  updateLivePrice();
  setStatus('Loading the designer…');

  if (edm) {
    edm.destroy();
    edm = null;
  }
  $('designer').innerHTML = '';

  const nonce = await getNonce();
  // Hide colours that don't fulfill in the customer's region, so the order routes
  // to a local Printful facility (no surprise cross-border shipment).
  const disabledColors = await disabledColorsForRegion(productId);
  // Seed the starting colour from a PDP swatch (?color=), once — applied via the
  // EDMConfig `preselectedColors` below. The colour name comes from the same
  // Printful source as the swatch (/api/pod/colors), so it matches the SDK's own
  // colour names. Worst case (no match) the customer just picks the colour.
  const startColor =
    !startColorApplied && deepLinkColor && productId === deepLinkProductId ? deepLinkColor : null;
  if (startColor) startColorApplied = true;
  // The /embed/nonces endpoint returns an opaque nonce (not a JWT), so it goes
  // in the `nonce` field. Passing it as `jwt` makes the SDK try to JWT-decode it
  // and fail with "Failed to decode JWT".
  edm = new EDM(
    {
      elementId: 'designer',
      nonce,
      externalProductId: `lds-${productId}-${Date.now()}`,
      initProduct: { productId, technique },
      debug: true,
    } as any,
    {
      clipartLayers: true,
      textLayers: true,
      fileLayers: true,
      useAnalyticsEvents: true,
      ...(disabledColors.length ? { disabledColors } : {}),
      // Seed the colour picked on the PDP swatch. The EDM SDK takes the starting
      // colour via `preselectedColors` (an EDMConfig field), NOT initProduct.color
      // — that field isn't in the SDK's initProduct type and was silently dropped,
      // which is why every deep link used to open on the default colour (Black).
      // The customer can still switch colours in the designer afterward.
      ...(startColor ? { preselectedColors: [startColor] } : {}),
    } as any,
    {
      onDesignStatusUpdate: (evt: any) => {
        designValid = Boolean(evt?.designValid);
        selectedVariantIds = Array.isArray(evt?.selectedVariantIds) ? evt.selectedVariantIds : [];
        usedPlacements = Array.isArray(evt?.usedPlacements) ? evt.usedPlacements : [];
        updateLivePrice();
        const over = usedPlacements.length > ADDON.maxPlacements;
        setSaveEnabled(designValid && !over);
        if (over) setStatus(`Up to ${ADDON.maxPlacements} print locations — remove one to continue.`);
        else setStatus(designValid ? 'Design looks good — ready to add to cart.' : 'Add a design to continue.');
      },
      onErrorUpdate: (evt: any) => setStatus(`Designer error: ${evt?.error ?? 'unknown'}`),
    } as any
  );

  await edm.init();
  setStatus(startColor ? `Starting in ${startColor}. Make it yours.` : 'Designer ready. Make it yours.');
}

// Build the mockup of the customer's saved design (the backend pulls the rendered
// image off the Printful template and re-hosts it on Shopify's CDN). Fails soft —
// a missing preview must never block checkout (the order is still correct: it
// carries the Printful template id).
async function generateMockupUrl(templateId: number | string): Promise<string | undefined> {
  try {
    const r = await fetch('/api/mockup/from-template', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId }),
    });
    if (!r.ok) return undefined;
    return (await r.json()).url as string;
  } catch (err) {
    console.error('[mockup] generation failed', err);
    return undefined;
  }
}

async function saveAndAddToCart() {
  if (!edm || !designValid) return;
  setSaveEnabled(false);
  setStatus('Saving your design…');

  const result = await edm.saveDesign();
  if (result.error || !result.data) {
    setStatus('Could not save the design. Try again.');
    setSaveEnabled(true);
    return;
  }

  const template = result.data.template;
  const variantId = selectedVariantIds[0] ?? null;

  // Resolve the customer's Printful variant -> the priced Shopify size-variant.
  let shopifyVariant: { shopifyVariantNumericId?: string; size?: string; color?: string } = {};
  if (variantId != null) {
    const vr = await fetch(`/api/shopify/variant?pfv=${variantId}`);
    if (vr.ok) shopifyVariant = await vr.json();
  }
  if (!shopifyVariant.shopifyVariantNumericId) {
    setStatus('That colour/size is not available for purchase yet. Pick another.');
    setSaveEnabled(true);
    return;
  }

  // Render + host the actual design mockup so it shows on the cart, the order, and
  // the confirmation email — traceability of exactly what was built.
  setStatus('Generating your preview…');
  const mockupUrl = await generateMockupUrl(template.id);

  // Line-item properties. Underscore-prefixed ones are hidden from the customer
  // (Shopify convention) and used by the order-router. "Colour" has no underscore
  // so it shows on the cart line — Size is already shown via the Shopify variant.
  // Guard: never exceed the placement cap.
  if (usedPlacements.length > ADDON.maxPlacements) {
    setStatus(`Up to ${ADDON.maxPlacements} print locations — remove one to continue.`);
    setSaveEnabled(true);
    return;
  }

  const properties: Record<string, string> = {
    _track: 'pod',
    _printful_template_id: String(template.id),
    _printful_variant_id: variantId ? String(variantId) : '',
    _technique: template.technique || currentTechnique,
    Method: techniqueName(currentTechnique),
    Colour: shopifyVariant.color ?? '',
  };
  if (mockupUrl) properties._mockup_preview_url = mockupUrl;

  // One surcharge line per EXTRA placement (base covers the first print). Flagged
  // _addon so the order-router doesn't re-send them to Printful as items.
  type CartItem = { id: number; quantity: number; properties: Record<string, string> };
  const addonItems: CartItem[] = extraPlacements()
    .map((p) => surchargeDef(p))
    .filter((d) => d.variantId)
    .map((d) => ({
      id: Number(d.variantId),
      quantity: 1,
      properties: { _addon: 'placement', Placement: d.label, _for: String(template.id) },
    }));
  // Decoration-method upcharge (e.g. embroidery), once per item.
  const method = methodSurcharge();
  if (method?.variantId) {
    addonItems.push({
      id: Number(method.variantId),
      quantity: 1,
      properties: { _addon: 'method', Method: method.label, _for: String(template.id) },
    });
  }
  const mainItem = { id: Number(shopifyVariant.shopifyVariantNumericId), quantity: 1, properties };
  const items = [mainItem, ...addonItems];

  // When embedded in the Shopify theme, hand the line items up to the parent so it
  // adds them to the real cart (see the page bridge / theme section).
  if (window.parent !== window) {
    // `items` drives the updated bridge (main + surcharge lines). variantId/properties
    // are a fallback so an older single-item bridge still adds at least the main item.
    window.parent.postMessage(
      { type: 'lds:add', items, variantId: mainItem.id, properties, productId: activeProductId },
      '*'
    );
    const extraNote = addonItems.length ? ` (+${addonItems.length} extra print${addonItems.length > 1 ? 's' : ''})` : '';
    setStatus(`Added to cart — ${shopifyVariant.color} / ${shopifyVariant.size}${extraNote}.`);
    setSaveEnabled(true);
    return;
  }

  // Standalone demo: echo the cart contract from the backend.
  const res = await fetch('/api/cart/add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      track: 'pod',
      printfulTemplateId: template.id,
      printfulVariantId: variantId,
      productId: activeProductId,
      colors: template.colors,
      sizes: template.sizes,
      technique: template.technique,
    }),
  });
  const cartLine = await res.json();
  renderCart(cartLine);
  setStatus('Added to cart.');
  setSaveEnabled(true);
}

function setStatus(msg: string) {
  $('status').textContent = msg;
}
function setSaveEnabled(on: boolean) {
  const btn = $('save') as HTMLButtonElement;
  btn.disabled = !on;
}

// --- POD browse (step 1): category tabs + visual product grid ---
let activeType = 'All';
// Live garment search (the studio's "Google-type" box). A query searches the WHOLE
// catalog (overrides the active category tab); tokenised so "long sleeve" matches every
// word against the product name + category. Clearing it returns to the active tab.
let searchQuery = '';
function searchTokens(): string[] {
  return searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
}
function matchesQuery(p: ProductOption, tokens: string[]): boolean {
  if (!tokens.length) return true;
  const hay = `${p.label} ${p.type ?? ''}`.toLowerCase();
  return tokens.every((t) => hay.includes(t));
}
function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string)
  );
}
function clearSearchInput(): void {
  searchQuery = '';
  const el = document.getElementById('pod-search-input') as HTMLInputElement | null;
  if (el) el.value = '';
}
let podView: 'catalog' | 'designer' = 'catalog';
// 'fill' = designer fills the screen (host sizes the iframe to the viewport).
// 'auto' = report content height so the iframe grows to fit (catalog/domestic).
let fitMode: 'auto' | 'fill' = 'auto';

// Ask the host page to size the iframe to the viewport so the studio fits on one
// screen (browse: grid scrolls inside; design: canvas + Save in view). Standalone
// (no host) just fills the browser window via the matching CSS.
function applyFill(view: 'browsing' | 'designing') {
  fitMode = 'fill';
  document.documentElement.classList.add('filling');
  document.body.classList.add('filling');
  document.body.classList.toggle('designing', view === 'designing');
  document.body.classList.toggle('browsing', view === 'browsing');
  if (window.parent !== window) window.parent.postMessage({ type: 'lds:fit', fill: true }, '*');
}
// Back to content-height (the Canadian-made rail/panel flows better tall).
function clearFill() {
  fitMode = 'auto';
  document.documentElement.classList.remove('filling');
  document.body.classList.remove('filling', 'designing', 'browsing');
  if (window.parent !== window) {
    window.parent.postMessage(
      { type: 'lds:height', height: Math.ceil(document.documentElement.scrollHeight) },
      '*'
    );
  }
}

function distinctTypes(): string[] {
  const seen = new Set<string>();
  for (const p of POD_PRODUCTS) if (p.type) seen.add(p.type);
  return ['All', ...Array.from(seen).sort()];
}

function renderCatTabs() {
  const wrap = $('cat-tabs');
  wrap.innerHTML = '';
  const types = distinctTypes();
  // Only one real category -> tabs add nothing.
  if (types.length <= 2) { wrap.style.display = 'none'; return; }
  wrap.style.display = '';
  for (const t of types) {
    const b = document.createElement('button');
    b.className = 'cat-tab' + (t === activeType ? ' is-active' : '');
    b.type = 'button';
    b.textContent = t;
    b.addEventListener('click', () => {
      activeType = t;
      clearSearchInput(); // picking a category clears any active search
      renderCatTabs();
      renderProductGrid();
    });
    wrap.appendChild(b);
  }
}

function buildProductCard(p: ProductOption): HTMLButtonElement {
  const card = document.createElement('button');
  card.className = 'pcard';
  card.type = 'button';
  const price = p.priceFrom != null ? `from $${p.priceFrom.toFixed(2)}` : (p.blurb ?? '');
  const colours = p.colors
    ? `<span class="pcard-colors">${p.colors} colour${p.colors > 1 ? 's' : ''}</span>`
    : '';
  const img = p.image
    ? `<img src="${p.image}" alt="${p.label}" loading="lazy" decoding="async">`
    : '<span class="ph">No preview</span>';
  card.innerHTML =
    `<span class="pcard-img">${img}</span>` +
    `<span class="pcard-body">` +
      `<span class="pcard-name">${p.label}</span>` +
      `<span class="pcard-price">${price}</span>` +
      colours +
    `</span>`;
  card.addEventListener('click', () => showDesigner(p.productId));
  return card;
}

// Render the whole grid; the page scrolls naturally (auto-height, no clipped rows) and
// images are lazy + async-decoded, so they only load as the customer scrolls into them —
// fast first paint without an internal scroll box (which was clipping the last row).
function renderProductGrid() {
  const grid = $('product-grid');
  grid.innerHTML = '';
  const tokens = searchTokens();
  // Search overrides the category tabs: a query searches the whole catalog.
  const base = tokens.length
    ? POD_PRODUCTS
    : activeType === 'All'
      ? POD_PRODUCTS
      : POD_PRODUCTS.filter((p) => p.type === activeType);
  const items = base.filter((p) => matchesQuery(p, tokens));
  if (!items.length) {
    grid.innerHTML = tokens.length
      ? `<p class="dom-hint">No garments match &ldquo;${escapeHtml(searchQuery.trim())}&rdquo;.</p>`
      : '<p class="dom-hint">No products in this category yet.</p>';
    return;
  }
  const frag = document.createDocumentFragment();
  for (const p of items) frag.appendChild(buildProductCard(p));
  grid.appendChild(frag);
}

function updateEditorHead(productId: number) {
  const p = POD_PRODUCTS.find((x) => x.productId === productId);
  $('eh-name').textContent = p?.label ?? 'Your product';
  $('eh-price').textContent = p?.priceFrom != null ? ` · from $${p.priceFrom.toFixed(2)}` : '';
}

function setLayout(name: 'catalog' | 'designer' | 'domestic') {
  const main = document.querySelector('main') as HTMLElement;
  main.classList.toggle('layout-domestic', name === 'domestic');
}

// Step 1: show the browse grid.
function showCatalog() {
  podView = 'catalog';
  clearFill(); // auto-height: the page scrolls, rows aren't clipped to the viewport
  $('mic-showcase').hidden = true;
  $('pod-catalog').hidden = false;
  $('stage').hidden = true;
  if (edm) { edm.destroy(); edm = null; }
  setLayout('catalog');
}

// Step 2: open the designer on a product.
function showDesigner(productId: number) {
  podView = 'designer';
  activeProductId = productId;
  $('mic-showcase').hidden = true;
  $('pod-catalog').hidden = true;
  $('stage').hidden = false;
  $('designer').style.display = '';
  $('pod-bar').style.display = '';
  // Hide the "back" control when locked to one product (arrived from a PDP).
  $('editor-head').hidden = lockToProduct && deepLinkProductId != null;
  updateEditorHead(productId);
  setLayout('designer');
  applyFill('designing');
  // Load this product's decoration methods, populate the picker, then mount.
  loadTechniques(productId)
    .then(() => { renderMethodSelect(); return mountDesigner(productId, currentTechnique); })
    .catch((e) => setStatus(String(e?.message ?? e)));
}

// What the prominent method bar says about the chosen technique's cost.
function methodHintText(key: string): string {
  const up = ADDON.methods?.[key];
  return up?.surcharge
    ? `${techniqueName(key)} adds $${up.surcharge.toFixed(2)} per item`
    : `${techniqueName(key)} — no extra charge`;
}
function updateMethodHint(): void {
  const hint = document.getElementById('method-hint');
  if (hint) hint.textContent = methodHintText(currentTechnique);
}
function renderMethodSelect(): void {
  const sel = document.getElementById('method-select') as HTMLSelectElement | null;
  const bar = document.getElementById('method-bar');
  const stat = document.getElementById('method-static');
  if (!sel) return;
  sel.innerHTML = '';
  for (const t of TECHNIQUES) {
    const o = document.createElement('option');
    o.value = t.key;
    o.textContent = t.name;
    sel.appendChild(o);
  }
  sel.value = currentTechnique;
  // Always show the method (it must be apparent); the dropdown only when there's a
  // real choice, otherwise show the single method as static text.
  const multi = TECHNIQUES.length > 1;
  sel.style.display = multi ? '' : 'none';
  if (stat) { stat.style.display = multi ? 'none' : ''; stat.textContent = multi ? '' : techniqueName(currentTechnique); }
  if (bar) bar.style.display = '';
  updateMethodHint();
}

function renderCart(line: Record<string, unknown>) {
  const panel = document.getElementById('cart');
  if (!panel) { setStatus('Added to cart.'); return; } // production embed has no demo cart
  panel.hidden = false;
  const row = document.createElement('pre');
  row.className = 'cart-line';
  row.textContent = JSON.stringify(line, null, 2);
  document.getElementById('cart-lines')?.prepend(row);
}

function setMode(mode: 'pod' | 'mic') {
  document.querySelectorAll<HTMLElement>('.mode').forEach((b) =>
    b.classList.toggle('is-active', b.dataset.mode === mode)
  );

  if (mode === 'pod') {
    $('mic-showcase').hidden = true;
    $('pod-catalog').hidden = false;
    renderCatTabs();
    renderProductGrid();
    // Deep-linked from a PDP -> jump straight into the designer.
    if (lockToProduct && deepLinkProductId != null) {
      showDesigner(deepLinkProductId);
    } else {
      showCatalog();
    }
    return;
  }

  // Made in Canada — a showcase that links out to the custom site for a quote.
  // No self-serve designer here; Canadian-made custom is a quoted, human-touch order.
  if (edm) { edm.destroy(); edm = null; }
  $('pod-catalog').hidden = true;
  $('stage').hidden = true;
  $('mic-showcase').hidden = false;
  clearFill(); // auto-height: full rows, page scrolls (no clipped cards)
  loadMicCatalog().then(renderMicGrid);
}

// --- Made in Canada showcase ---
type MicProduct = { title: string; image?: string | null; priceFrom?: number };
let MIC_PRODUCTS: MicProduct[] = [];

async function loadMicCatalog(): Promise<void> {
  if (MIC_PRODUCTS.length) return;
  try {
    const res = await fetch('/api/mic/catalog');
    if (res.ok) MIC_PRODUCTS = (await res.json()) as MicProduct[];
  } catch {
    /* keep empty -> the CTA still funnels to the custom site */
  }
}

function renderMicGrid(): void {
  const grid = $('mic-grid');
  grid.innerHTML = '';
  if (!MIC_PRODUCTS.length) {
    grid.innerHTML =
      '<p class="dom-hint">Use “Get a custom quote” above and our team will help with Canadian-made custom apparel.</p>';
    return;
  }
  for (const p of MIC_PRODUCTS) {
    const card = document.createElement('a');
    card.className = 'pcard';
    card.href = CUSTOM_SITE_URL;
    card.target = '_blank';
    card.rel = 'noopener';
    const img = p.image
      ? `<img src="${p.image}" alt="${p.title}" loading="lazy">`
      : '<span class="ph">No preview</span>';
    card.innerHTML =
      `<span class="pcard-img"><span class="mic-badge">${MAPLE_SVG} Made in Canada</span>${img}</span>` +
      `<span class="pcard-body">` +
        `<span class="pcard-name">${p.title}</span>` +
        `<span class="pcard-price">Get a custom quote →</span>` +
      `</span>`;
    grid.appendChild(card);
  }
}

// When embedded in the Shopify theme, report our content height to the parent so
// it can size the iframe to fit. Without this the iframe is a fixed height and the
// designer gets clipped — especially in a narrow CMS column. Pairs with the page's
// `lds:height` listener (theme/sections/design-studio.liquid).
function startHeightReporter(): void {
  if (window.parent === window) return;
  let last = 0;
  const post = () => {
    if (fitMode !== 'auto') return; // designer is in fill mode; host owns the height
    const h = Math.ceil(document.documentElement.scrollHeight);
    if (h && Math.abs(h - last) > 8) {
      last = h;
      window.parent.postMessage({ type: 'lds:height', height: h }, '*');
    }
  };
  // Catches layout changes from mode/product switches.
  new ResizeObserver(post).observe(document.body);
  window.addEventListener('resize', post);
  // Belt-and-suspenders for the async EDM mount (cross-origin, so its internal
  // growth isn't observable directly). Cheap: only posts when the height changes.
  setInterval(post, 1000);
  post();
}

async function main() {
  startHeightReporter();
  ($('save') as HTMLButtonElement).addEventListener('click', () => {
    saveAndAddToCart().catch((e) => setStatus(String(e?.message ?? e)));
  });
  document.querySelectorAll<HTMLButtonElement>('.mode').forEach((b) =>
    b.addEventListener('click', () => setMode(b.dataset.mode as 'pod' | 'mic'))
  );
  $('back-to-products').addEventListener('click', () => showCatalog());

  // Live garment search — filter the grid as the customer types.
  const searchInput = document.getElementById('pod-search-input') as HTMLInputElement | null;
  searchInput?.addEventListener('input', () => {
    searchQuery = searchInput.value;
    renderProductGrid();
  });

  // Decoration method change -> re-mount the designer with the chosen technique.
  // The EDM technique is fixed at init, so switching must re-mount (resets the canvas).
  // Guard it: don't silently wipe a design the customer already built.
  const methodSel = document.getElementById('method-select') as HTMLSelectElement | null;
  methodSel?.addEventListener('change', () => {
    const next = methodSel.value;
    if (next === currentTechnique) return;
    if (designValid && !confirm('Switching decoration method will reset your current design. Continue?')) {
      methodSel.value = currentTechnique; // revert the picker
      return;
    }
    currentTechnique = next;
    updateMethodHint();
    mountDesigner(activeProductId, currentTechnique).catch((e) => setStatus(String(e?.message ?? e)));
  });

  // Point the Made-in-Canada CTAs at the custom site (quote / talk to a person).
  for (const id of ['mic-cta-top', 'mic-cta-bottom']) {
    const a = document.getElementById(id) as HTMLAnchorElement | null;
    if (a) a.href = CUSTOM_SITE_URL;
  }

  await Promise.all([loadPodCatalog(), loadAddonPricing()]);
  if (deepLinkProductId == null) activeProductId = POD_PRODUCTS[0]?.productId ?? activeProductId;
  // Deep-links (?product=) are POD; otherwise start on the browse grid.
  setMode('pod');
}

main();
